import React, { useState } from 'react';
import { ArrowRight, Loader2, ShieldCheck, UserPlus } from 'lucide-react';
import { useToast } from './ui/Toaster';
import { http } from '../services/http';
import { toUserError } from '../services/errors';

interface BootstrapAdminPageProps {
  onBootstrapped: () => void;
  onGoToLogin?: () => void;
  organizationName?: string;
}

export const BootstrapAdminPage: React.FC<BootstrapAdminPageProps> = ({ onBootstrapped, onGoToLogin, organizationName }) => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const resolvedOrgName = String(organizationName || 'АрхивЛинк');
  const orgInitial = (resolvedOrgName.trim()[0] || 'А').toUpperCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const formName = String(fd.get('name') || name || '');
    const formUsername = String(fd.get('username') || username || '');
    const formPassword = String(fd.get('password') || password || '');
    const formPassword2 = String(fd.get('password2') || password2 || '');
    if (!formName || !formUsername || !formPassword) return;

    const n = String(formName).trim();
    if (!n) {
      addToast('Введите имя', 'error');
      return;
    }

    const uname = String(formUsername).trim();
    if (!uname) {
      addToast('Введите логин', 'error');
      return;
    }

    const unameOk = /^[a-zA-Z0-9._-]{3,30}$/.test(uname);
    if (!unameOk) {
      addToast('Логин должен быть 3-30 символов: латиница/цифры/._-', 'error');
      return;
    }

    if (String(formPassword).length < 6) {
      addToast('Пароль слишком короткий (минимум 6 символов)', 'error');
      return;
    }

    if (formPassword !== formPassword2) {
      addToast('Пароли не совпадают', 'error');
      return;
    }

    setLoading(true);
    try {
      setName(formName);
      setUsername(formUsername);
      setPassword(formPassword);
      setPassword2(formPassword2);
      const resp = await http.post<{ token: string; user: any }>('/bootstrap/admin', { name: n, username: uname, password: formPassword });
      try { localStorage.setItem('al_jwt', resp.token); } catch {}
      try { localStorage.setItem('al_user', JSON.stringify(resp.user)); } catch {}
      try {
        if ((resp.user as any)?.mustChangePassword) localStorage.setItem('al_must_change_password', '1');
        else localStorage.removeItem('al_must_change_password');
      } catch {}
      addToast('Администратор создан', 'success');
      onBootstrapped();
    } catch (err: any) {
      const ue = toUserError(err, 'Не удалось создать администратора');
      if (ue.code === 'bootstrap_already_completed') {
        addToast(ue.message, 'info');
        try { onGoToLogin && onGoToLogin(); } catch {}
      } else {
        addToast(ue.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-8">
      <div className="mx-auto w-full max-w-md text-center">
        <div className="mx-auto h-16 w-16 bg-brand-600 rounded-xl flex items-center justify-center shadow-lg transform -rotate-6">
          <span className="text-3xl font-bold text-white">{orgInitial}</span>
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900 tracking-tight">
          {resolvedOrgName}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Первичная настройка
        </p>
      </div>

      <div className="mt-8 mx-auto w-full max-w-md">
        <div className="bg-white py-8 px-10 shadow-xl shadow-gray-200/50 rounded-xl border border-gray-100">
          <div className="flex items-center justify-center gap-2 text-brand-700 font-semibold mb-6">
            <UserPlus className="w-5 h-5" />
            Создание первого администратора
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">Имя</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Логин</label>
              <div className="mt-1">
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">3-30 символов: латиница, цифры, точка, подчёркивание, дефис</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Пароль</label>
              <div className="mt-1">
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Повторите пароль</label>
              <div className="mt-1">
                <input
                  type="password"
                  name="password2"
                  autoComplete="new-password"
                  required
                  value={password2}
                  onChange={e => setPassword2(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">Создать администратора <ArrowRight className="w-4 h-4"/></span>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4"/> Создание пользователя только один раз
                </span>
              </div>
            </div>
          </div>

          {onGoToLogin && (
            <button
              type="button"
              onClick={() => onGoToLogin()}
              className="mt-6 w-full text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Перейти ко входу
            </button>
          )}
        </div>
        <p className="text-center text-xs text-gray-400 mt-4">
          &copy; 2024 {resolvedOrgName} Enterprise. Все права защищены.
        </p>
      </div>
    </div>
  );
};
